import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";

/* ============================================
   ORCHESTRIO COMPONENT LIBRARY
   Clean, structured components
   ============================================ */

// ==================== HEADER ====================

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "/services" },
    { name: "Work", href: "/work" },
    { name: "Process", href: "/process" },
    { name: "Pricing", href: "/pricing" },
    { name: "About", href: "/about" },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? 'bg-[#1E1E1E]/98 backdrop-blur-sm border-b border-[#2E2E2E]' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/orchestrio-logo.png" 
              alt="Orchestrio" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm transition-colors ${
                  isActive(link.href) 
                    ? 'text-[#F4F6F8]' 
                    : 'text-[#9CA3AF] hover:text-[#F4F6F8]'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <Link 
              to="/contact" 
              className="text-sm text-[#F4F6F8] bg-[#2F5D8C] hover:bg-[#5F8FB8] px-4 py-2 rounded transition-colors"
              data-testid="nav-contact"
            >
              Get in touch
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#F4F6F8]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#2E2E2E]">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`block py-3 text-sm ${
                  isActive(link.href) ? 'text-[#F4F6F8]' : 'text-[#9CA3AF]'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link 
              to="/contact" 
              className="block py-3 mt-4 text-sm text-center bg-[#2F5D8C] rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get in touch
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== FOOTER ====================

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const links = {
    services: [
      { name: "Marketing Websites", href: "/services#websites" },
      { name: "Web Applications", href: "/services#web-apps" },
      { name: "Integrations", href: "/services#integrations" },
      { name: "Support", href: "/services#support" },
    ],
    company: [
      { name: "About", href: "/about" },
      { name: "Work", href: "/work" },
      { name: "Process", href: "/process" },
      { name: "Pricing", href: "/pricing" },
    ],
  };

  return (
    <footer className="bg-[#1A1A1A] border-t border-[#2E2E2E]" data-testid="main-footer">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src="/orchestrio-logo.png" alt="Orchestrio" className="h-8 w-auto" />
            </Link>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              Digital systems, built properly.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              {links.services.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-sm text-[#9CA3AF] hover:text-[#F4F6F8] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {links.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-sm text-[#9CA3AF] hover:text-[#F4F6F8] transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Contact</h4>
            <address className="not-italic space-y-2 text-sm text-[#9CA3AF]">
              <p>hello@orchestrio.co.uk</p>
            </address>
            <Link 
              to="/contact" 
              className="inline-block mt-4 text-sm text-[#5F8FB8] hover:text-[#F4F6F8] transition-colors"
            >
              Get in touch
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2E2E2E] mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#6B7280] text-xs">
            © {currentYear} Orchestrio. Registered in England & Wales.
          </p>
          <div className="flex gap-4">
            <Link to="/privacy" className="text-[#6B7280] hover:text-[#9CA3AF] text-xs transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-[#6B7280] hover:text-[#9CA3AF] text-xs transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== HERO SECTION ====================

export const Hero = ({ 
  marker = "UK-Based Digital Agency",
  title,
  titleAccent = true,
  description,
  primaryCta,
  primaryCtaLink = "/contact",
  secondaryCta,
  secondaryCtaLink = "/contact",
  image,
  children
}) => {
  return (
    <section className="section-hero" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in">
            {marker && (
              <div className="section-marker mb-6">{marker}</div>
            )}
            <h1 className={`text-display font-bold mb-6 ${titleAccent ? 'headline-accent' : ''}`}>
              {title}
            </h1>
            {description && (
              <p className="text-body-lg text-[#8A8A8A] mb-8 max-w-xl">{description}</p>
            )}
            {(primaryCta || secondaryCta) && (
              <div className="flex flex-col sm:flex-row gap-4">
                {primaryCta && (
                  <Link to={primaryCtaLink} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-primary-cta">
                    {primaryCta} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {secondaryCta && (
                  <Link to={secondaryCtaLink} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="hero-secondary-cta">
                    {secondaryCta}
                  </Link>
                )}
              </div>
            )}
            {children}
          </div>
          
          {image && (
            <div className="lg:col-span-5 hidden lg:block animate-fade-in delay-200">
              <div className="relative">
                <div className="aspect-square bg-[#1C1C1C] border border-[#2E2E2E] overflow-hidden">
                  <img src={image} alt="" className="w-full h-full object-cover opacity-80" />
                </div>
                {/* Industrial corner accent */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#5F8FB8]" aria-hidden="true" />
                <div className="absolute -top-4 -left-4 w-16 h-16 border-2 border-[#2E2E2E]" aria-hidden="true" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ==================== SERVICE CARD ====================

export const ServiceCard = ({ icon, title, description, features = [], href }) => {
  const Wrapper = href ? Link : 'div';
  
  return (
    <Wrapper 
      to={href} 
      className="card-service group"
      data-testid={`service-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {icon && (
        <div className="w-14 h-14 bg-[#2F5D8C]/15 flex items-center justify-center text-[#5F8FB8] mb-6 group-hover:bg-[#2F5D8C] group-hover:text-[#F4F6F8] transition-colors rounded">
          {icon}
        </div>
      )}
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-[#8A8A8A] text-sm leading-relaxed mb-4">{description}</p>
      {features.length > 0 && (
        <ul className="space-y-2">
          {features.map((feature, i) => (
            <li key={i} className="text-sm text-[#F5F4EF] flex items-center gap-2">
              <span className="w-1 h-1 bg-[#5F8FB8]" />
              {feature}
            </li>
          ))}
        </ul>
      )}
      {href && (
        <div className="mt-6 flex items-center gap-2 text-sm text-[#5F8FB8] opacity-0 group-hover:opacity-100 transition-opacity">
          Learn more <ChevronRight className="w-4 h-4" />
        </div>
      )}
    </Wrapper>
  );
};

// ==================== CASE STUDY CARD ====================

export const CaseStudyCard = ({ image, category, client, title, description, results = [], href }) => {
  return (
    <article className="card-case-study" data-testid="case-study-card">
      <div className="card-image">
        <img src={image} alt={title} />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="badge badge-accent">{category}</span>
          <span className="text-[#8A8A8A] text-xs">—</span>
          <span className="text-[#8A8A8A] text-xs">{client}</span>
        </div>
        <h3 className="text-lg font-bold mb-2 group-hover:text-[#5F8FB8] transition-colors">{title}</h3>
        <p className="text-[#8A8A8A] text-sm mb-4 line-clamp-2">{description}</p>
        {results.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {results.map((result, i) => (
              <span key={i} className="px-3 py-1 bg-[#0E0E0E] border border-[#2E2E2E] text-xs">
                {result}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

// ==================== TESTIMONIAL CARD ====================

export const TestimonialCard = ({ quote, author, role, company, rating = 5 }) => {
  return (
    <div className="card-testimonial" data-testid="testimonial-card">
      {rating > 0 && (
        <div className="flex gap-1 mb-4 relative z-10">
          {[...Array(rating)].map((_, i) => (
            <svg key={i} className="w-4 h-4 text-[#5F8FB8] fill-current" viewBox="0 0 20 20">
              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
            </svg>
          ))}
        </div>
      )}
      <blockquote className="text-[#F5F4EF] leading-relaxed mb-6 relative z-10">
        "{quote}"
      </blockquote>
      <footer className="relative z-10">
        <div className="font-bold">{author}</div>
        <div className="text-[#8A8A8A] text-sm">{role}, {company}</div>
      </footer>
    </div>
  );
};

// ==================== PRICING CARD ====================

export const PricingCard = ({ 
  name, 
  description, 
  price, 
  priceNote, 
  duration, 
  features = [], 
  cta = "Get Started",
  ctaLink = "/contact",
  featured = false,
  onCtaClick = null
}) => {
  const handleClick = (e) => {
    if (onCtaClick) {
      onCtaClick(name, cta);
    }
  };

  return (
    <div className={`card-pricing ${featured ? 'featured' : ''}`} data-testid={`pricing-card-${name?.toLowerCase()}`}>
      {featured && (
        <div className="text-[#5F8FB8] text-xs uppercase tracking-wider font-bold mb-4">Most Popular</div>
      )}
      <h3 className="text-xl font-bold mb-2">{name}</h3>
      <p className="text-[#8A8A8A] text-sm mb-6">{description}</p>
      
      <div className="mb-6">
        {priceNote && <span className="text-[#8A8A8A] text-xs block">{priceNote}</span>}
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">{typeof price === 'number' ? `£${price.toLocaleString()}` : price}</span>
          {typeof price === 'number' && <span className="text-[#8A8A8A]">+VAT</span>}
        </div>
        {duration && <span className="text-[#8A8A8A] text-sm">{duration}</span>}
      </div>

      <ul className="space-y-3 mb-8">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <svg className="w-4 h-4 text-[#5F8FB8] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Link 
        to={ctaLink}
        onClick={handleClick}
        className={`block text-center py-3 font-semibold text-sm transition-colors rounded ${
          featured 
            ? 'bg-[#2F5D8C] text-[#F4F6F8] hover:bg-[#5F8FB8]' 
            : 'border border-[#2E2E2E] hover:bg-[#2E2E2E]'
        }`}
      >
        {cta}
      </Link>
    </div>
  );
};

// ==================== FAQ ACCORDION ====================

export const FAQAccordion = ({ items = [] }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4" data-testid="faq-accordion">
      {items.map((item, index) => (
        <div key={index} className="border border-[#2E2E2E]">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1C1C1C] transition-colors"
          >
            <span className="font-bold pr-4">{item.question}</span>
            <ChevronDown className={`w-5 h-5 text-[#5F8FB8] flex-shrink-0 transition-transform ${
              openIndex === index ? 'rotate-180' : ''
            }`} />
          </button>
          <div className={`overflow-hidden transition-all ${
            openIndex === index ? 'max-h-96' : 'max-h-0'
          }`}>
            <div className="px-6 pb-6 text-[#8A8A8A] text-sm leading-relaxed">
              {item.answer}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== STATS ROW ====================

export const StatsRow = ({ stats = [] }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x divide-[#2E2E2E]" data-testid="stats-row">
      {stats.map((stat, i) => (
        <div key={i} className="text-center md:px-8">
          <div className="text-4xl md:text-5xl font-bold text-[#5F8FB8]">{stat.value}</div>
          <div className="text-[#8A8A8A] text-sm uppercase tracking-wider mt-2">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

// ==================== SECTION HEADER ====================

export const SectionHeader = ({ marker, title, description, centered = false, children }) => {
  return (
    <div className={`mb-16 ${centered ? 'text-center' : ''}`}>
      {marker && <div className="section-marker mb-4">{marker}</div>}
      <h2 className="text-headline font-bold headline-accent">{title}</h2>
      {description && (
        <p className={`text-body-lg text-[#8A8A8A] mt-4 ${centered ? 'max-w-2xl mx-auto' : 'max-w-xl'}`}>
          {description}
        </p>
      )}
      {children}
    </div>
  );
};

// ==================== CTA SECTION ====================

export const CTASection = ({ 
  title, 
  description, 
  primaryCta = "Get in touch",
  primaryCtaLink = "/contact",
  secondaryCta,
  secondaryCtaLink = "/contact"
}) => {
  return (
    <section className="section section-bordered" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-headline font-bold headline-accent mb-6">{title}</h2>
        {description && (
          <p className="text-body-lg text-[#8A8A8A] mb-8 max-w-2xl mx-auto">{description}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={primaryCtaLink} className="btn-primary inline-flex items-center justify-center gap-2">
            {primaryCta} <ArrowRight className="w-4 h-4" />
          </Link>
          {secondaryCta && (
            <Link to={secondaryCtaLink} className="btn-secondary inline-flex items-center justify-center gap-2">
              {secondaryCta}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

// ==================== BLOG CARD ====================

export const BlogCard = ({ image, date, title, excerpt, slug, tags = [] }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <article className="card-case-study" data-testid={`blog-card-${slug}`}>
      {image && (
        <Link to={`/blog/${slug}`}>
          <div className="card-image">
            <img src={image} alt={title} />
          </div>
        </Link>
      )}
      <div className="p-6">
        <div className="flex items-center gap-4 mb-3 text-xs text-[#8A8A8A]">
          {date && <span>{formatDate(date)}</span>}
        </div>
        <Link to={`/blog/${slug}`}>
          <h3 className="text-lg font-bold mb-2 hover:text-[#5F8FB8] transition-colors">{title}</h3>
        </Link>
        {excerpt && <p className="text-[#8A8A8A] text-sm mb-4 line-clamp-2">{excerpt}</p>}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="badge badge-neutral">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

// ==================== PROCESS STEP ====================

export const ProcessStep = ({ number, icon, title, description, activities = [] }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-t border-[#2E2E2E] first:border-t-0" data-testid={`process-step-${number}`}>
      <div className="lg:col-span-2">
        <div className="flex items-center gap-4 lg:flex-col lg:items-start">
          <span className="number-marker">{number}</span>
          {icon && (
            <div className="w-12 h-12 bg-[#5F8FB8]/10 flex items-center justify-center text-[#5F8FB8] lg:mt-4">
              {icon}
            </div>
          )}
        </div>
      </div>
      <div className="lg:col-span-6">
        <h3 className="text-2xl font-bold mb-4">{title}</h3>
        <p className="text-[#8A8A8A] leading-relaxed">{description}</p>
      </div>
      {activities.length > 0 && (
        <div className="lg:col-span-4">
          <h4 className="text-sm uppercase tracking-wider text-[#5F8FB8] mb-4">Key Activities</h4>
          <ul className="space-y-2">
            {activities.map((activity, i) => (
              <li key={i} className="text-sm flex items-center gap-2">
                <span className="w-1 h-1 bg-[#5F8FB8]" />
                {activity}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default {
  Header,
  Footer,
  Hero,
  ServiceCard,
  CaseStudyCard,
  TestimonialCard,
  PricingCard,
  FAQAccordion,
  StatsRow,
  SectionHeader,
  CTASection,
  BlogCard,
  ProcessStep
};
